import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { brmAPI } from "@/services/api";
import { User, Bell, LogOut, Save, Pencil } from "lucide-react";
import {
  TailAdminDetailRow,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const BRMProfile = ({ onLogout }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [form, setForm] = useState({ name: "", username: "", region: "" });

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await brmAPI.getProfile();
      const data = res.data;
      setProfile(data);
      setForm({
        name: data.name || "",
        username: data.username || "",
        region: data.region || "Lagos",
      });
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSaving(true);
    try {
      await brmAPI.updateProfile({
        name: form.name.trim(),
        username: form.username.trim() || undefined,
        region: form.region.trim(),
      });
      toast.success("Profile updated");
      setEditing(false);
      await fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      onLogout();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const memberSince = profile?.createdAt || profile?.created_at;
  const referralCode = profile?.referralCode || profile?.referral_code;

  return (
    <div className="px-4 py-6 space-y-6 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900" data-testid="profile-title">
            Profile
          </h1>
          <p className="text-sm text-gray-500">Your platform identity and account settings</p>
        </div>
        {!loading && !editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="profile-edit-btn">
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
        )}
      </div>

      <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-xs">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="w-16 h-16 rounded-full" />
            <Skeleton className="h-6 w-32" />
          </div>
        ) : editing ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Display name</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username (handle)</Label>
              <div className="flex items-center gap-1">
                <span className="text-gray-400">@</span>
                <Input
                  id="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value.replace(/\s/g, "") })}
                  placeholder="yourname"
                  data-testid="profile-username-input"
                />
              </div>
              <p className="text-xs text-gray-500">3–30 chars · letters, numbers, dots, underscores</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                data-testid="profile-region-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="profile-save-btn">
                <Save className="w-4 h-4 mr-1" />
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center">
              <User className="w-8 h-8 text-brand-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900" data-testid="profile-name">
                {profile?.name}
              </h2>
              {profile?.username && (
                <p className="text-sm text-brand-600" data-testid="profile-username">@{profile.username}</p>
              )}
              <TailAdminStatusBadge tone={profile?.status === "active" ? "success" : "neutral"}>
                {profile?.status === "active" ? "Active BRM" : profile?.status}
              </TailAdminStatusBadge>
            </div>
          </div>
        )}
      </section>

      <TailAdminPanel title="Account Details" action={null}>
        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))
          ) : (
            <>
              <TailAdminDetailRow label="Phone Number" value={<span data-testid="profile-phone">{profile?.phone}</span>} />
              <TailAdminDetailRow label="Region" value={<span data-testid="profile-region">{profile?.region}</span>} />
              {referralCode && (
                <TailAdminDetailRow label="Referral Code" value={<span data-testid="profile-referral">{referralCode}</span>} />
              )}
              <TailAdminDetailRow label="Member Since" value={<span data-testid="profile-since">{formatDate(memberSince)}</span>} />
            </>
          )}
        </div>
      </TailAdminPanel>

      <TailAdminPanel title="Settings" action={null}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
              <Bell className="w-5 h-5 text-brand-500" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Push Notifications</p>
              <p className="text-xs text-gray-500">Receive alerts for commissions</p>
            </div>
          </div>
          <Switch
            checked={notifications}
            onCheckedChange={setNotifications}
            data-testid="notifications-toggle"
          />
        </div>
      </TailAdminPanel>

      <Button
        variant="outline"
        className="w-full h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={handleLogout}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Logout
      </Button>

      <p className="text-center text-xs text-gray-500">Proovam BRM v1.1.0</p>
    </div>
  );
};

export default BRMProfile;
